/* import __COLOCATED_TEMPLATE__ from './list-descriptors.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-bare-strings */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { StandaloneTicketAttribute } from 'embercom/objects/standalone/data-fields/common';
import type AppService from 'embercom/services/app-service';
import type Session from 'embercom/services/session';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import type TicketType from 'embercom/models/inbox/ticket-type';
import { taskFor } from 'ember-concurrency-ts';
import type { IllustrativeIconName } from '@intercom/pulse/components/ic-illustrative-icon';
import { put } from 'embercom/lib/ajax';

type Args = {
  conversationAttributeDescriptors: ConversationAttributeDescriptor[];
  paywallIsActive: boolean;
  compact?: boolean;
  emptyState?: {
    icon: IllustrativeIconName;
    title: string;
  };
  isInNewSettingsContainer: boolean;
  onClickNewDescriptor?: () => void;
  onDescriptorCreated?: () => void;
  ticketType?: TicketType;
};

type StandaloneConversationAttributeDescriptor =
  | StandaloneTicketAttribute
  | ConversationAttributeDescriptor;

export default class ListDescriptors extends Component<Args> {
  // @Compact is an argument that is used to define if the edit button should appear over the name when hovering the att,
  // or if it should be on the right of the table. It also controls if we open a modal or sidedrawer when editing att.
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare router: RouterService;

  @tracked selectedAttribute?: ConversationAttributeDescriptor = undefined;
  @tracked showDescriptorDetailsModal = false;
  @tracked showDescriptorArchiveModal = false;
  @tracked selectedType = 'any';
  @tracked selectedTypes: string[] = [];

  @tracked currentTab = 'live';
  @tracked conditionsEditorTargetAttribute: ConversationAttributeDescriptor | null = null;
  @tracked sideDrawerStartTab: string | null = null;

  get standaloneConversationAttributeDescriptors(): StandaloneConversationAttributeDescriptor[] {
    let standaloneConversationAttributeDescriptors: StandaloneConversationAttributeDescriptor[];

    standaloneConversationAttributeDescriptors = this._displayableStandalonePlatformTicketFields;
    standaloneConversationAttributeDescriptors = standaloneConversationAttributeDescriptors.concat(
      this.liveConversationAttributeDescriptors.filter((attr) => {
        return !this._standalonePlatformTicketFields.some(
          (ticketField) => ticketField.intercomCvdaIdentifier === attr.id,
        );
      }),
    );

    return standaloneConversationAttributeDescriptors.sortBy('name');
  }

  get _displayableStandalonePlatformTicketFields(): StandaloneTicketAttribute[] {
    return this._standalonePlatformTicketFields
      .filter((ticketField) => {
        return (ticketField.synchronized || ticketField.required) && ticketField.id !== 'tags';
      })
      .map((ticketField) => {
        return new StandaloneTicketAttribute({
          standalonePlatform: this._standalonePlatform,
          ticketField,
          intercomCvda: this.liveConversationAttributeDescriptors.find(
            (attr) => attr.id === ticketField.intercomCvdaIdentifier,
          ),
        });
      });
  }

  get _standalonePlatform() {
    return this.app.standalone_platforms[0].platform_type;
  }

  get _standalonePlatformTicketFields() {
    return this.appService.mainStandalonePlatform.ticketFields;
  }

  get liveConversationAttributeDescriptors(): ConversationAttributeDescriptor[] {
    let liveConversationAttributeDescriptors = this.args.conversationAttributeDescriptors
      .rejectBy('archived')
      .reject(this.hasReferenceToUndefinedOrSystemDefinedObjectType);

    return liveConversationAttributeDescriptors.sortBy('order');
  }

  get archivedConversationAttributeDescriptors(): ConversationAttributeDescriptor[] {
    return this.args.conversationAttributeDescriptors
      .filterBy('archived')
      .reject(this.hasReferenceToUndefinedOrSystemDefinedObjectType);
  }

  get selectedConversationAttributeDescriptors() {
    if (this.currentTab === 'archived') {
      return this.archivedConversationAttributeDescriptors;
    } else if (this.app.canUseStandalone) {
      return this.standaloneConversationAttributeDescriptors;
    } else {
      return this.liveConversationAttributeDescriptors;
    }
  }

  get availableTypes() {
    let descriptors = this.selectedConversationAttributeDescriptors || [];
    let types: string[] = descriptors
      .map((attr) => {
        let descriptor: ConversationAttributeDescriptor =
          'intercomCvda' in attr ? attr.intercomCvda : attr;
        if (!descriptor) {
          return undefined;
        }
        if (
          (descriptor.displayableDataType || descriptor.dataType) === 'List' &&
          descriptor.isClassificationAttribute
        ) {
          return 'Fin Attributes';
        }
        return descriptor.displayableDataType || descriptor.dataType;
      })
      .filter(Boolean)
      .uniq();
    return types;
  }

  get filteredConversationAttributeDescriptors() {
    let filtered = this.selectedConversationAttributeDescriptors;
    if (this.selectedTypes.length) {
      filtered = filtered.filter((attr) => {
        let descriptor: ConversationAttributeDescriptor =
          'intercomCvda' in attr ? attr.intercomCvda : attr;
        let type = descriptor.displayableDataType || descriptor.dataType;
        let isCategory = type === 'List' && descriptor.isClassificationAttribute;
        let matchesCategory = this.selectedTypes.includes('Fin Attributes') && isCategory;
        let matchesType = this.selectedTypes.includes(type);
        return matchesCategory || matchesType;
      });
    }
    // If nothing is selected, show all
    return filtered;
  }

  get liveTabActive() {
    return this.currentTab === 'live';
  }

  get dateLabel() {
    return this.liveTabActive ? this.columnLabel('created') : this.columnLabel('archived');
  }

  get dateValuePath() {
    return this.liveTabActive ? 'createdAt' : 'archivedAt';
  }

  get app() {
    return this.appService.app;
  }

  get hasEnoughAttributes() {
    return this.liveConversationAttributeDescriptors.filter((attr) => !attr.isBuiltIn).length >= 2;
  }

  columnLabel(key: string) {
    return this.intl.t(`settings.conversation-attributes.table.${key}`);
  }

  get columns() {
    let isSortable = false;

    let tableColumns: {
      label: string;
      valuePath: string;
      isSortable?: boolean;
      minWidth?: string;
      width?: string;
      labelCanWrap?: boolean;
    }[] = [
      {
        label: this.columnLabel('name'),
        valuePath: 'nameLowercase',
        isSortable,
        minWidth: '200px',
      },
      {
        label: this.columnLabel('format'),
        valuePath: 'dataType',
        isSortable,
      },
    ];
    tableColumns.push({ label: this.dateLabel, valuePath: this.dateValuePath, isSortable });

    if (!this.app.canUseStandalone) {
      if (this.isConversationTicketType) {
        tableColumns.push({
          label: this.columnLabel('visibility'),
          valuePath: 'visibleTo',
          minWidth: '160px',
          width: '350px',
        });
      }

      if (this.liveTabActive) {
        tableColumns = [{ label: '', valuePath: 'order', width: '0' }, ...tableColumns];
      }

      tableColumns.push({
        label: this.columnLabel('required'),
        valuePath: 'required',
        isSortable,
      });

      if (!this.isConversationTicketType) {
        tableColumns.push({
          label: this.columnLabel('teammate-options'),
          valuePath: 'teammateOptions',
          labelCanWrap: true,
          minWidth: this.args.compact ? '130px' : undefined,
        });
        tableColumns.push({
          label: this.columnLabel('customer-options'),
          valuePath: 'customerOptions',
          labelCanWrap: true,
          minWidth: this.args.compact ? '120px' : undefined,
        });
      }

      tableColumns.push({
        label: this.columnLabel('conditions'),
        valuePath: 'conditionsCount',
        minWidth: '120px',
      });
    }

    tableColumns.push({
      label: '',
      valuePath: 'actionButtons',
      width: '0',
    });

    return tableColumns;
  }

  get isConversationTicketType() {
    if (this.args.ticketType === undefined) {
      return true;
    }

    return this.args.ticketType.isConversationTicketType;
  }

  get emptyState() {
    return (
      this.args.emptyState || {
        icon: 'conversation',
        title: this.intl.t('settings.data.conversation-attributes.empty-state.title'),
      }
    );
  }

  get standaloneEmptyStateDescription() {
    let integrationUrl = this.router.urlFor('apps.app.settings.standalone.integration');

    // TODO: SA Generic platform migration – Move to platform.componentData
    if (this.appService.app.hasStandalonePlatform('zendesk')) {
      return this.intl.t('standalone.settings.data.conversation-tickets.description.zendesk', {
        integrationUrl,
        htmlSafe: true,
      }) as string;
    } else if (this.appService.app.hasStandalonePlatform('salesforce')) {
      return this.intl.t('standalone.settings.data.conversation-tickets.description.salesforce', {
        integrationUrl,
        htmlSafe: true,
      }) as string;
    }

    return;
  }

  get categoriesRoute() {
    if (this.app.isStandaloneApp) {
      return 'apps.app.standalone.attributes';
    }
    return 'apps.app.automation.fin-ai-agent.attributes';
  }

  humanReadableCombinedStatus(visible: boolean, required: boolean) {
    if (visible && required) {
      return 'Visible & required';
    } else if (visible) {
      return 'Visible';
    } else {
      return 'Not visible';
    }
  }

  @action
  editAttribute(attribute: ConversationAttributeDescriptor) {
    this.sideDrawerStartTab = null;
    this.selectedAttribute = attribute;
    this.showDescriptorDetailsModal = true;
  }

  @action
  editAttributeConditions(attribute: ConversationAttributeDescriptor) {
    this.sideDrawerStartTab = 'conditions';
    this.selectedAttribute = attribute;
    this.showDescriptorDetailsModal = true;
  }

  @action
  archiveAttribute() {
    this.showDescriptorArchiveModal = true;
  }

  @action
  restoreAttribute(attribute: ConversationAttributeDescriptor) {
    taskFor(this.unarchiveAttribute).perform(attribute);
  }

  @action
  switchTab(tab: string) {
    this.currentTab = tab;
  }

  @action
  selectLiveTab() {
    this.currentTab = 'live';
  }

  @action
  selectArchivedTab() {
    this.currentTab = 'archived';
  }

  @action
  closeModals() {
    this.showDescriptorDetailsModal = false;
    this.showDescriptorArchiveModal = false;
    this.conditionsEditorTargetAttribute = null;
  }

  @action
  saveConditions(attribute: ConversationAttributeDescriptor) {
    taskFor(this.doSaveConditions).perform(attribute);
  }

  @action
  onTypeFilterChange(selectedTypes: string[]) {
    this.selectedTypes = selectedTypes;
  }

  @task({ drop: true })
  *doSaveConditions(attribute: ConversationAttributeDescriptor) {
    try {
      yield attribute.conditions.save();
      this.notificationsService.notifyConfirmation(
        this.notification('conditions-success', { attribute: attribute.name }),
      );
      if (this.archivedConversationAttributeDescriptors.length === 0) {
        this.currentTab = 'live';
      }
      this.closeModals();
    } catch (error) {
      let errorMessage = error?.jqXHR?.responseJSON?.errors;
      let errorCodes = error?.jqXHR?.responseJSON?.error_codes;
      if (errorCodes) {
        for (let [field, error] of Object.entries(errorCodes)) {
          let messageKey = `settings.conversation-attributes.notifications.errors.${field}_${error}`;
          if (this.intl.lookup(messageKey)) {
            errorMessage = this.intl.t(messageKey);
          }
        }
      }

      attribute.rollbackAttributes();

      if (errorMessage) {
        this.notificationsService.notifyError(errorMessage);
      } else {
        this.notificationsService.notifyError(
          this.notification('conditions-failed', { attribute: attribute.name }),
        );
      }
    }
  }

  @task({ drop: true })
  *reorder(
    updatedOrder: ConversationAttributeDescriptor[],
    draggedWrapper: ConversationAttributeDescriptor,
  ) {
    if (this.liveTabActive) {
      let oldOrder = [...this.liveConversationAttributeDescriptors];

      try {
        let orderChanged = this.updateOrderValues(updatedOrder);
        if (!orderChanged) {
          return;
        }
        yield put(`/ember/conversation_attributes/descriptors/${draggedWrapper.id}/reorder`, {
          order: draggedWrapper.order,
          app_id: this.appService.app.id,
          ticket_type_id: draggedWrapper.ticketTypeId,
        });

        this.notificationsService.notifyConfirmation(
          this.notification('reorder-success', { attribute: draggedWrapper.name }),
        );
      } catch (error) {
        this.notificationsService.notifyError(
          this.notification('reorder-failed', { attribute: draggedWrapper.name }),
        );
        this.updateOrderValues(oldOrder);
      }
    }
  }

  updateOrderValues(order: ConversationAttributeDescriptor[]) {
    let orderChanged = false;
    let numOfDefaultAttributes = this.args.conversationAttributeDescriptors
      .rejectBy('archived')
      .filter((d) => d.isBuiltIn).length;
    // default attributes don't get returned in updatedOrder, that's why we increment the each order by the number of default attributes
    // default attributes will always be at the top
    this.liveConversationAttributeDescriptors.forEach((d) => {
      if (order.indexOf(d) >= 0 && d.order !== order.indexOf(d) + numOfDefaultAttributes) {
        d.set('order', order.indexOf(d) + numOfDefaultAttributes);
        orderChanged = true;
      }
    });
    return orderChanged;
  }

  hasReferenceToUndefinedOrSystemDefinedObjectType(
    attribute: ConversationAttributeDescriptor,
    _index: number,
    _array: ConversationAttributeDescriptor[],
  ) {
    return (
      isPresent(attribute.reference) &&
      (attribute.referencedObjectType === undefined ||
        attribute.referencedObjectType.isSystemDefined)
    );
  }

  @task({ drop: true })
  *unarchiveAttribute(attribute: ConversationAttributeDescriptor) {
    try {
      attribute.archived = false;
      attribute.order =
        Math.max(...this.liveConversationAttributeDescriptors.map((d) => d.order)) + 1;
      yield attribute.save();
      this.notificationsService.notifyConfirmation(
        this.notification('unarchive-success', { attribute: attribute.name }),
      );
      if (this.archivedConversationAttributeDescriptors.length === 0) {
        this.currentTab = 'live';
      }
    } catch (error) {
      attribute.rollbackAttributes();
      this.notificationsService.notifyError(
        this.notification('unarchive-failed', { attribute: attribute.name }),
      );
    }
  }

  notification(translationKey: string, attributes: Record<string, string>) {
    return this.intl.t(
      `settings.conversation-attributes.notifications.${translationKey}`,
      attributes,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Settings::ConversationAttributes::ListDescriptors': typeof ListDescriptors;
    'settings/conversation-attributes/list-descriptors': typeof ListDescriptors;
  }
}
